import { Text } from '@user-portal/wikr-components';
import { useTranslation } from 'react-i18next';
import { useCallback, useMemo, useState } from 'react';

import { selectCurrentUser } from 'store/user/selectors';
import { GET_USER_PERSONALISATION, UPDATE_USER_PERSONALISATION } from 'store/user/actionTypes';
import { updateUserPersonalisationRequest } from 'store/user/actions';
import { createProcessingSelector } from 'store/loading/selectors';

import { ProblemZone } from 'constants/user';

import useModals from 'modals/hooks';
import { useDispatch, useSelector } from 'hooks';

import { checkZonesEqual, filterSelectedZones } from './helpers';

import Modal from 'components/Modal/Modal';
import { CustomPrimaryButton, OverlineButton } from 'components/Buttons';
import Pill from './components/Pill';

import * as S from './styled';

const ProblemZonesModal = () => {
    const dispatch = useDispatch();

    const { problem_zones } = useSelector(selectCurrentUser);
    const { t } = useTranslation();
    const { closeModal } = useModals();

    const [selectedZones, setSelectedZones] = useState<ProblemZone[]>(problem_zones ?? []);

    const isLoading: boolean = useSelector(
        createProcessingSelector([UPDATE_USER_PERSONALISATION, GET_USER_PERSONALISATION])
    );

    const areZonesEqual = useMemo(
        () => checkZonesEqual(selectedZones, problem_zones ?? []),
        [selectedZones, problem_zones]
    );

    const isSaveButtonDisabled = !selectedZones.length || areZonesEqual;

    const handleSelectZone = useCallback(
        (zone: ProblemZone) => {
            const filteredZones = filterSelectedZones(selectedZones, zone);

            setSelectedZones(filteredZones);
        },
        [selectedZones]
    );

    const onSave = () => {
        dispatch(
            updateUserPersonalisationRequest({
                payload: {
                    problem_zones: selectedZones,
                },
                onSuccess: () => {
                    closeModal();
                },
            })
        );
    };

    return (
        <Modal
            onClose={closeModal}
            withRoundedCorners={false}
            dataLocator="problemAreaModal"
            dataTestId="problemAreaModal"
            mobileFullScreen
            isOpen
            shouldCloseOnOverlayClick
        >
            <S.ContentWrapper data-locator="problemAreasModal" data-testid="problemAreasModal">
                <Text type="h4" center>
                    {t('account.planSettings.problemAreas')}
                </Text>
                <S.ModalContent data-locator="pillsWrapper" data-testid="pillsWrapper">
                    {Object.values(ProblemZone).map((zone) => (
                        <Pill<ProblemZone>
                            onClick={handleSelectZone}
                            id={zone}
                            title={t(`basics.user.problemZone.${zone}--dynamicKey`)}
                            key={zone}
                            isActive={selectedZones.includes(zone)}
                        />
                    ))}
                </S.ModalContent>
                <div>
                    <CustomPrimaryButton
                        dataLocator="save"
                        disabled={isSaveButtonDisabled}
                        mb={20}
                        textColor="text-main"
                        onClick={onSave}
                        isLoading={isLoading}
                    >
                        {t('basics.save')}
                    </CustomPrimaryButton>
                    <OverlineButton dataLocator="cancelBtn" onClick={closeModal} text={t('basics.cancel')} />
                </div>
            </S.ContentWrapper>
        </Modal>
    );
};

export default ProblemZonesModal;
